import { useInfiniteQuery } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { InvoicesViewApiAdapter, InvoiceStoragesFilters } from '../adapters/InvoicesViewApiAdapter';
import { InvoiceStorage } from '../models/InvoicesView';

interface UseInvoiceStoragesProps {
  invoiceId: number;
  searchFilter?: { filters: any[]; logic: string };
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  enabled?: boolean;
}

/**
 * useInvoiceStorages - Hook para cargar storages de una factura con paginación infinita
 *
 * Utiliza React Query (useInfiniteQuery) para manejar la carga paginada de storages
 * desde el endpoint /invoices/invoices/{id}/storages con soporte para:
 * - Infinite scroll
 * - Filtros avanzados
 * - Sorting
 * - Cache automático
 */
export const useInvoiceStorages = ({
  invoiceId,
  searchFilter,
  search,
  sortBy = 'product_id',
  sortDirection = 'asc',
  enabled = true,
}: UseInvoiceStoragesProps) => {
  const invoiceNumericId = Number(invoiceId);
  const isEnabled = enabled && Number.isFinite(invoiceNumericId) && invoiceNumericId > 0;

  const httpClient = createHttpClient(apiClientWH);
  const invoicesViewAdapter = new InvoicesViewApiAdapter(httpClient);

  const filters: InvoiceStoragesFilters = {
    invoiceId: invoiceNumericId,
    searchFilter,
    search,
    sortBy,
    sortDirection,
  };

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['invoiceStorages', filters],
    queryFn: ({ pageParam = 1 }) =>
      invoicesViewAdapter.getInvoiceStoragesPaginated({
        ...filters,
        page: pageParam,
        limit: 25,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 2 * 60 * 1000, // 2 minutos
    gcTime: 5 * 60 * 1000, // 5 minutos
    enabled: isEnabled,
    placeholderData: (previousData) => previousData,
  });

  const storages: InvoiceStorage[] =
    data?.pages.flatMap((page) => page.storages) ?? [];

  const total = data?.pages[0]?.total ?? 0;

  return {
    storages,
    isLoading,
    isFetching,
    error: error ? (error as Error).message : null,
    total,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

